import { courseReviewDue } from '../lib/courseReview.ts';
import type { Pair } from '../lib/i18n.ts';
import { lessonNo } from '../lib/levels.ts';
import { emptyProgress } from '../lib/progress.ts';
import { LOCAL_PROFILE } from '../lib/route.ts';
import type { AppCtx } from './ctx.ts';
import { renderDiagnostics } from './diagnostics.ts';
import { button, h } from './dom.ts';
import { renderAdmin } from './admin.ts';
import { renderAttemptPanel } from './attemptPanel.ts';
import { renderChatPanel } from './chatPanel.ts';
import { renderLangPanel } from './langToggle.ts';
import { renderPairing } from './pairing.ts';
import { renderHomeHead } from './homeHead.ts';
import { renderSwitchProfile } from './profileBar.ts';
import { renderRankingButton } from './rankingScreen.ts';
import { renderWeekCard } from './weekScreen.ts';
import { langOf, onLang, txText } from './i18n.ts';
import { icon, mascot } from './kit.ts';
import { FINISHED_SCREEN, HOME_SCREENS, finishedTitle } from '../lib/screens.ts';
import { syncLine } from '../lib/syncView.ts';
import { dailyVariant } from '../lib/variants.ts';

// Napisy dla dziecka idą za przełącznikiem EN/PL (patch-33). Blok „Dla rodzica” (details i wszystko w środku) zostaje po polsku.
const T = {
  nextSub: { en: 'Want one more? Or come back tomorrow.', pl: 'Chcesz jeszcze jedną? Możesz też wrócić jutro.' },
  allDone: { en: 'All lessons are done!', pl: 'Wszystkie lekcje zrobione!' },
  allDoneHint: { en: 'More lessons are coming soon.', pl: 'Kolejne lekcje pojawią się wkrótce.' },
  courseDone: { en: 'All lessons are done 🎉', pl: 'Wszystkie lekcje zrobione 🎉' },
  courseSub: { en: 'Six steps: listen, scene, words, practice', pl: 'Sześć kroków: słuchanie, scenka, słówka, ćwiczenia' },
  pairHint: {
    en: '🔑 Exact pronunciation checking is off. Parent: open “Dla rodzica” (the parent area, in Polish) and pair this device.',
    pl: '🔑 Dokładna ocena wymowy jest wyłączona. Rodzic: „Dla rodzica” ▸ Serwer ▸ Sparuj to urządzenie.',
  },
} as const satisfies Record<string, Pair>;

/** Napis zależny od języka, ale bez form płci: imię dziecka może zawierać „{” albo „|”, więc nie przepuszczamy go przez `say`. */
function liveRaw<E extends HTMLElement>(ctx: AppCtx, el: E, text: (lang: 'en' | 'pl') => string): E {
  el.textContent = text(langOf(ctx));
  const off = onLang(ctx, (lang) => {
    if ((el as { isConnected?: boolean }).isConnected === false) off();
    else el.textContent = text(lang);
  });
  return el;
}

const METHOD_LABEL = {
  azure: 'Azure (dokładna ocena wymowy)',
  browser: 'przeglądarka (uproszczona ocena)',
  self: 'samoocena (brak automatycznej oceny)',
} as const;

/**
 * Ekran główny — jedna ścieżka nauki (patch-48, SPEC §23.27): „Dziś: lekcja N” (pierwsza nieukończona lekcja kursu), po dzisiejszej lekcji „Kolejna lekcja: N”,
 * oraz „Powtórka dnia (N)”, gdy czekają zaległe słowa. Dowolną lekcję rodzic uruchamia w „Dla rodzica”.
 */
export function renderHome(ctx: AppCtx): void {
  const progress = ctx.getProgress();
  const today = ctx.today();
  const doneToday = progress.lastLessonDate === today;

  const account = ctx.account;
  const family = account?.mode() === 'family';
  const course = ctx.course && ctx.startCourse ? ctx.course : null;
  const startCourse = ctx.startCourse;
  const courseDone = (id: string): boolean => progress.course?.done[id] !== undefined;
  const nextCourse = course?.lessons.find((l) => !courseDone(l.id));
  const no = (l: { id: string; seq: number }): number => lessonNo(course?.lessons ?? [], l);

  // --- górny pasek: profil i liczby (seria, gwiazdki, słówka) ---
  const head = renderHomeHead(ctx);

  // --- środek: powitanie z maskotką (5 wariantów) albo „Świetna robota” po dzisiejszej lekcji; wszystkie lekcje zrobione = osobny ekran ---
  let hero: HTMLElement;
  const cta: (HTMLElement | null)[] = [];
  if (doneToday) {
    const name = account?.profile()?.name ?? '';
    hero = h('div', { class: 'hero finished', 'data-testid': 'hero', 'data-pose': FINISHED_SCREEN.pose },
      h('div', { class: 'hero-text' },
        liveRaw(ctx, h('h1', { class: 'hand hand-title', 'data-testid': 'done-today' }), (lang) => finishedTitle(name, lang)),
        txText(ctx, h('p', { class: 'hand hand-text' }), FINISHED_SCREEN.text),
      ),
      mascot(FINISHED_SCREEN.pose),
    );
    // Kolejna lekcja jest dostępna od razu (SPEC §23.12): pięć słów na lekcję, a tempo wybiera dziecko — druga i trzecia lekcja tego samego dnia nie są blokowane.
    if (nextCourse && startCourse) {
      cta.push(h('button', { type: 'button', class: 'cta-bar', 'data-testid': 'next-lesson', onclick: () => startCourse(nextCourse) },
        icon('calendar'), h('span', { class: 'cta-text' }, txText(ctx, h('span', { class: 'cta-title' }), { en: `Next lesson: ${no(nextCourse)}`, pl: `Kolejna lekcja: ${no(nextCourse)}` }), txText(ctx, h('span', { class: 'cta-sub' }), T.nextSub)), icon('arrow')));
    } else if (course) {
      cta.push(txText(ctx, h('p', { class: 'muted course-done', 'data-testid': 'course-done' }), T.courseDone));
    }
  } else if (nextCourse && startCourse) {
    const n = HOME_SCREENS.length;
    const v = HOME_SCREENS[dailyVariant(today, n, account?.profile()?.id ?? '')]!;
    hero = h('div', { class: 'hero', 'data-testid': 'hero', 'data-pose': v.pose },
      h('div', { class: 'hero-text' }, txText(ctx, h('h1', { class: 'hand hand-title' }), v.title), txText(ctx, h('p', { class: 'hand hand-text' }), v.text)),
      mascot(v.pose),
    );
    cta.push(h('button', { type: 'button', class: 'cta-bar', 'data-testid': 'start', onclick: () => startCourse(nextCourse) },
      icon('calendar'), h('span', { class: 'cta-text' }, txText(ctx, h('span', { class: 'cta-title' }), { en: `Today: lesson ${no(nextCourse)}`, pl: `Dziś: lekcja ${no(nextCourse)}` }), txText(ctx, h('span', { class: 'cta-sub' }), { en: `${nextCourse.theme} · ${T.courseSub.en}`, pl: `${nextCourse.themePl} · ${T.courseSub.pl}` })), icon('arrow')));
  } else {
    hero = h('div', { class: 'hero finished', 'data-testid': 'hero' }, h('div', { class: 'hero-text' }, txText(ctx, h('h1', { class: 'hand hand-title', 'data-testid': 'all-done' }), T.allDone), txText(ctx, h('p', { class: 'hand hand-text' }), T.allDoneHint)), mascot(FINISHED_SCREEN.pose));
  }

  // Lekcję kursu wybiera też rodzic w „Dla rodzica” (dowolną, także już zrobioną).
  const coursePicker = course && startCourse
    ? h('div', { class: 'row', 'data-testid': 'course-picker-box' },
        h('select', { 'data-testid': 'course-picker', 'aria-label': 'Wybierz lekcję' }, ...course.lessons.map((l) => h('option', { value: l.id }, `Lekcja ${no(l)} — ${l.themePl}${courseDone(l.id) ? ' ✓' : ''}`))),
        button('Uruchom wybraną lekcję', () => {
          const sel = ctx.root.querySelector<HTMLSelectElement>('[data-testid="course-picker"]');
          const l = course.lessons.find((x) => x.id === sel?.value);
          if (l) startCourse(l);
        }, { class: 'btn ghost', testid: 'start-course-picked' }),
      )
    : null;

  // „Powtórka dnia” (patch-47): zaległe słowa z lekcji kursu, ale nie więcej niż zostało w dziennym limicie; gdy nic nie czeka, przycisku nie ma
  const extraItems: HTMLElement[] = [];
  const reviewDue = ctx.course && ctx.startReview ? courseReviewDue({ progress, lessons: ctx.course.lessons, today, profile: account?.profile()?.id ?? LOCAL_PROFILE }) : 0;
  if (reviewDue > 0) extraItems.push(txText(ctx, button('', () => ctx.startReview?.(), { class: 'btn ghost', testid: 'review-course' }), { en: `Review of the day (${reviewDue})`, pl: `Powtórka dnia (${reviewDue})` }));
  const extra = extraItems.length > 0 ? h('div', { class: 'secondary-actions' }, ...extraItems) : null;

  const methodLine = h('p', { class: 'muted', 'data-testid': 'method' });
  // Podpowiedź na ekranie głównym tylko wtedy, gdy serwer jest, ale nie zna tego urządzenia — dziecko nie musi nic robić, ale rodzic zobaczy, czemu ocena jest uproszczona.
  const pairHint = txText(ctx, h('p', { class: 'muted pair-hint', 'data-testid': 'pair-hint', hidden: true }), T.pairHint);
  const refreshMethod = () => {
    const chain = ctx.chain();
    methodLine.textContent = `Ocena wymowy: ${METHOD_LABEL[chain.primary]}`;
    pairHint.hidden = chain.azure !== 'unpaired';
  };
  refreshMethod();
  void ctx.whenChain().then(refreshMethod); // sonda Azure kończy się chwilę po starcie

  ctx.root.replaceChildren(
    h('main', { class: 'stage home', 'data-testid': 'home-screen' },
      head,
      hero,
      ...cta,
      renderWeekCard(ctx),
      extra,
      pairHint,
      h('div', { class: 'home-foot' },
        h('details', { class: 'parent' },
          h('summary', null, 'Dla rodzica'),
          methodLine,
          account && renderAccountInfo(ctx),
          renderPairing(ctx, refreshMethod),
          button('🛠 Panel rodzica: urządzenia i lekcje', () => renderAdmin(ctx), { class: 'btn ghost', testid: 'open-admin' }),
          renderLangPanel(ctx),
          renderDiagnostics(ctx),
          renderAttemptPanel(ctx),
          renderChatPanel(ctx),
          coursePicker,
          family
            ? h('p', { class: 'muted', 'data-testid': 'reset-hint' }, 'Wyzerowanie postępu dziecka: „Panel rodzica” ▸ Profile dzieci (wymaga PIN-u, żeby dziecko nie skasowało sobie postępu przypadkiem).')
            : button('Wyzeruj postęp', () => {
                if (confirm('Na pewno wyzerować cały postęp (słówka, gwiazdki, serię)?')) {
                  ctx.setProgress(emptyProgress());
                  ctx.goHome();
                }
              }, { class: 'btn link danger', testid: 'reset-progress' }),
        ),
        h('div', { class: 'foot-actions' }, renderRankingButton(ctx), renderSwitchProfile(ctx)),
      ),
    ),
  );
}

/** Konta w sekcji „Dla rodzica”: czyj to profil, stan zapisu postępu na serwerze i ewentualny problem z serwerem profili. */
function renderAccountInfo(ctx: AppCtx): HTMLElement {
  const acc = ctx.account;
  const box = h('div', { class: 'account-info', 'data-testid': 'account-info' });
  if (!acc) return box;
  if (acc.mode() === 'family') {
    const line = h('p', { class: 'muted', 'data-testid': 'sync-line' });
    const paint = (): void => {
      line.textContent = syncLine(acc.syncState(), acc.syncDetail(), acc.syncedAt(), new Date());
    };
    paint();
    const off = acc.onSync(() => {
      if ((line as { isConnected?: boolean }).isConnected === false) off();
      else paint();
    });
    box.append(h('p', { class: 'muted', 'data-testid': 'profile-line' }, `Profil: ${acc.profile()?.name ?? ''}`), line, button('Synchronizuj teraz', () => void acc.syncNow(), { class: 'btn link', testid: 'sync-now' }));
  } else if (acc.problem()) {
    box.append(h('p', { class: 'adm-error', role: 'alert', 'data-testid': 'account-problem' }, `Serwer profili nie działa: ${acc.problem()}. Aplikacja pracuje lokalnie (postęp tylko w tej przeglądarce).`));
  }
  return box;
}
